import { DeleteOutline, LaunchOutlined } from "@mui/icons-material";
import { Box, Card, CardContent, IconButton, Stack, TextField, Typography } from "@mui/material";
import { format } from "date-fns";
import { Trans, useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router";

import { GqlPermissions, GqlPlannerV2VisitPossibleDaysStatus, GqlPlannerVisitStatus } from "~/__generated__";
import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { formatDateTime } from "~/modules/app/util/formatters";
import useHasTenantPermission from "~/modules/auth/hooks/useHasPermission";
import { IsRejectedAlert } from "~/shared/components/IsRejectedAlert";
import VisitStatusChip from "~/shared/components/VisitStatusChip";
import { VisitStatusTimeline } from "~/shared/components/VisitStatusTimeline";

import { MapEditorPlannerAreaPanel_VisitListItemFragment } from "./fragments.generated";
import NumberOfFieldworkersInput from "./NumberOfFieldworkersInput";

type PlannerVisitListItemProps = {
    visit: MapEditorPlannerAreaPanel_VisitListItemFragment;
    onDeleteClick: () => void;
    onUpdate: (plannerVisitId: string, requiredFieldworkers: number) => Promise<void>;
};

type VisitAction = { type: "delete" } | { type: "link"; to: string };

function getVisitAction(visit: MapEditorPlannerAreaPanel_VisitListItemFragment, isAdmin: boolean): VisitAction | null {
    // TODO: this value needs to be sourced from the backend
    const isDeletable = isAdmin && visit.deletedAt === null && (visit.startTime === null || visit.startTime === undefined);
    if (isDeletable) {
        return { type: "delete" };
    }

    switch (visit.status) {
        case GqlPlannerVisitStatus.Approved:
            return {
                type: "link",
                to: generatePath("/c/:clientSlug/p/:projectSlug/visits/:visitId", {
                    clientSlug: visit.plannerArea.project.client.slug,
                    projectSlug: visit.plannerArea.project.slug,
                    visitId: visit.id,
                }),
            };

        case GqlPlannerVisitStatus.AwaitingApproval:
        case GqlPlannerVisitStatus.AwaitingReport:
        case GqlPlannerVisitStatus.AwaitingObservationApproval:
        case GqlPlannerVisitStatus.AwaitingConfirm:
            if (!isAdmin) return null;
            return {
                type: "link",
                to: generatePath("/c/:clientSlug/p/:projectSlug/review-visit/:visitId", {
                    clientSlug: visit.plannerArea.project.client.slug,
                    projectSlug: visit.plannerArea.project.slug,
                    visitId: visit.id,
                }),
            };

        case GqlPlannerVisitStatus.Planned:
            if (!isAdmin) return null;
            if (visit.startTime === null || visit.startTime === undefined) return null;
            return {
                type: "link",
                to: generatePath("/planner/:date/areas/:areaId/visits/:visitId", {
                    date: format(parseStringToDate(visit.startTime as string), "yyyy-MM-dd"),
                    areaId: visit.plannerArea.id,
                    visitId: visit.id,
                }),
            };

        default:
            return null;
    }
}

export default function PlannerVisitListItem({ visit, onDeleteClick, onUpdate }: PlannerVisitListItemProps) {
    const { t } = useTranslation("mapEditor");
    const isAdmin = useHasTenantPermission(GqlPermissions.Administrator);

    const handleOnFieldworkersChange: React.ChangeEventHandler<HTMLInputElement> = event => {
        const nextValue = Number(event.target.value);

        onUpdate(visit.id, nextValue);
    };

    let title = String(visit.protocolVisit?.number ?? 1);
    if (visit.protocolVisit?.name) title = `${title} - ${visit.protocolVisit?.name}`;
    const visitAction = getVisitAction(visit, isAdmin);

    return (
        <Card component="li" variant="outlined" sx={{ margin: 2 }}>
            <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Stack alignContent="center" direction="row" justifyContent="space-between">
                    <Typography variant="h6" component="span">
                        {title}
                    </Typography>
                    {visitAction?.type === "delete" && (
                        <IconButton onClick={onDeleteClick} size="small">
                            <DeleteOutline />
                        </IconButton>
                    )}
                    {visitAction?.type === "link" && (
                        <IconButton component={Link} to={visitAction.to} size="small">
                            <LaunchOutlined />
                        </IconButton>
                    )}
                </Stack>
                <VisitStatusChip
                    deleted={visit.deletedAt !== null && visit.deletedAt !== undefined}
                    value={visit.status}
                    sx={{ alignSelf: "flex-start" }}
                />
                {visit.plannerStatus !== GqlPlannerV2VisitPossibleDaysStatus.Planned ? (
                    <Box sx={{ marginInline: 1 }}>
                        <VisitStatusTimeline
                            plannerStatus={visit.plannerStatus}
                            possibleDays={visit.possibleDays}
                            selectedDate={new Date()}
                            variant="minimal"
                        />
                    </Box>
                ) : (
                    <span>
                        <Trans
                            i18nKey="mapEditorVisitLists.startTimeText"
                            values={{ datetime: formatDateTime(parseStringToDate(visit.startTime as string)) }}
                            components={{ strong: <strong /> }}
                            ns="mapEditor"
                        />
                    </span>
                )}
                <NumberOfFieldworkersInput
                    disabled={!isAdmin}
                    onChange={handleOnFieldworkersChange}
                    label={t("mapEditorVisitLists.workers")}
                    required
                    value={visit.requiredFieldworkers}
                />
                {isAdmin && visit.routeStartOffset !== null && visit.routeStartDirection !== null && (
                    <Stack direction="row" spacing={1}>
                        <TextField disabled label={t("mapEditorVisitLists.routeStartOffset")} value={visit.routeStartOffset} />
                        <TextField
                            disabled
                            label={t("mapEditorVisitLists.routeStartDirection")}
                            value={visit.routeStartDirection}
                        />
                    </Stack>
                )}
            </CardContent>
            {visit.deletedAt && visit.deletedByUser && (
                <IsRejectedAlert
                    actor={visit.deletedByUser}
                    color="success"
                    date={visit.deletedAt}
                    reason={visit.deletionReason ?? undefined}
                    variant="deleted"
                />
            )}
        </Card>
    );
}
